import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/base/Seo";

const faqs = [
  {
    q: "番木瓜漫画每日什么时候更新上架内容？",
    a: "番木瓜漫画最新上架内容每日更新，通常在晚间集中上新。关注本站「最新上架」板块即可第一时间看到当天的新作，热门新番也会同步更新进度。",
  },
  {
    q: "新番导视里的作品都是当季的吗？",
    a: "是的，新番导视板块聚焦本季新作，按季度整理当季开播与开载的作品，并标注重点推荐与题材标签，帮你快速锁定值得追的作品。",
  },
  {
    q: "连载新开和普通连载有什么区别？",
    a: "连载新开特指刚刚开启连载的长篇作品，适合想从头追更、见证故事起步的读者。本站会集中展示这类新开连载，方便第一时间发现新坑。",
  },
  {
    q: "如何查看作品的更新进度？",
    a: "进入作品详情页即可查看当前连载话数、更新频率与最近更新状态，热门新番与连载新开板块也会同步展示关键追更信息。",
  },
  {
    q: "作品评分是怎么来的？",
    a: "番木瓜漫画的评分为读者追更口碑的综合参考，结合作品热度、讨论度与读者反馈形成，供选番时参考，不代表绝对权威。",
  },
  {
    q: "可以在这里推荐自己喜欢的新番吗？",
    a: "当然可以。欢迎通过「联系我们」页面把你喜欢的新作推荐给我们，我们会评估后纳入新番导视或最新上架内容，让好作品被更多人看见。",
  },
  {
    q: "浏览网站需要注册或付费吗？",
    a: "浏览番木瓜漫画的全部栏目与内容均无需注册、无需付费，新番导视、最新上架、热门新番与连载新开均可免费查阅。",
  },
  {
    q: "遇到作品信息有误或链接打不开怎么办？",
    a: "如果发现作品信息有误、图片异常或链接无法打开，可通过「联系我们」页面反馈给我们，我们会尽快核实并修正。",
  },
];

export default function FaqPage() {
  const [open, setOpen] = useState<number>(0);

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Seo
        title="常见问题 - 番木瓜漫画"
        description="番木瓜漫画常见问题解答：每日更新上架时间、新番导视说明、连载新开区别、作品评分来源与使用方式等，帮你更顺畅地追更本站最新漫画内容。"
        keywords="番木瓜漫画,番木瓜漫画常见问题,番木瓜漫画每日更新,番木瓜漫画使用帮助"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="面包屑" className="text-xs text-background-200/70">
              <Link to="/" className="transition hover:text-primary-300">
                首页
              </Link>
              <span className="mx-2">/</span>
              <span className="text-primary-300">常见问题</span>
            </nav>
            <h1 className="mt-3 font-heading text-3xl text-background-50 md:text-4xl">
              追更常见问题
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-background-100/85 md:text-base">
              关于番木瓜漫画的更新节奏、栏目内容与使用方式的常见疑问，我们都整理在这里。没找到答案？欢迎直接联系我们。
            </p>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
            <div className="grid grid-cols-1 gap-3">
              {faqs.map((f, i) => (
                <div
                  key={f.q}
                  className="overflow-hidden rounded-2xl border border-background-200 bg-background-100/60"
                >
                  <button
                    onClick={() => setOpen(open === i ? -1 : i)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                    aria-expanded={open === i}
                  >
                    <span className="text-sm font-bold text-foreground-900 md:text-base">
                      {f.q}
                    </span>
                    <i
                      className={`shrink-0 text-lg text-primary-600 transition ${
                        open === i ? "ri-subtract-line" : "ri-add-line"
                      }`}
                    ></i>
                  </button>
                  {open === i && (
                    <div className="border-t border-background-200 bg-background-50 px-5 py-4">
                      <p className="text-sm leading-7 text-foreground-700">
                        {f.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl bg-foreground-950 px-6 py-10 text-center sm:flex-row sm:justify-between sm:text-left">
              <div>
                <h2 className="font-heading text-xl text-background-50">
                  没找到你想问的？
                </h2>
                <p className="mt-2 text-sm text-background-100/80">
                  把你的问题告诉我们，我们会尽快回复。
                </p>
              </div>
              <Link
                to="/contact"
                className="flex shrink-0 items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
              >
                前往联系我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}